import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { ChevronDown, ChevronRight, GeoAlt, Map, Search } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import { makeApiRequests } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';
import {
  buildSitesBySalesLocationId,
  filterSalesLocations,
  formatSalesLocationAddress,
  getSalesLocationRegion,
  getSalesLocationTitle
} from '../../helpers/salesLocationHelpers';
import Loader from '../Loader';
import './SalesLocationSelectModal.scss';

const SalesLocationLotRow = ({ site, isActive, onHover, onHoverEnd, onSelect }) => (
  <li className="sales-location-lot">
    <button
      type="button"
      className={`sales-location-lot-btn${isActive ? ' is-active' : ''}`}
      onClick={onSelect}
      onMouseEnter={onHover}
      onMouseLeave={onHoverEnd}
      onFocus={onHover}
      onBlur={onHoverEnd}
      aria-label={`Open sitemap for ${site.name}`}
    >
      <span className="sales-location-lot-icon" aria-hidden>
        <Map size={14} />
      </span>
      <span className="sales-location-lot-label">{site.name}</span>
      <ChevronRight className="sales-location-lot-chevron" size={14} aria-hidden />
    </button>
  </li>
);

const SalesLocationLotsPanel = ({
  title,
  sites,
  isExpanded,
  hoveredSiteId,
  onSiteHover,
  onSiteHoverEnd,
  onSiteSelect,
  location
}) => (
  <div
    className="sales-location-children"
    role="region"
    aria-label={`Lots for ${title}`}
    aria-hidden={!isExpanded}
  >
    <div className="sales-location-children-inner">
      <ul className="sales-location-lots">
        {sites.map(site => (
          <SalesLocationLotRow
            key={site._id}
            site={site}
            isActive={hoveredSiteId === site._id}
            onHover={() => onSiteHover(site._id)}
            onHoverEnd={onSiteHoverEnd}
            onSelect={e => {
              e.stopPropagation();
              onSiteSelect(site, location);
            }}
          />
        ))}
      </ul>
    </div>
  </div>
);

const SalesLocationGroup = ({
  location,
  title,
  address,
  region,
  showAddress,
  lotCount,
  hasMultipleLots,
  isExpanded,
  isHovered,
  sites,
  hoveredSiteId,
  onParentActivate,
  onParentHover,
  onParentHoverEnd,
  onSiteHover,
  onSiteHoverEnd,
  onSiteSelect
}) => (
  <article
    className={`sales-location-group${isExpanded ? ' is-expanded' : ''}${isHovered && !isExpanded ? ' is-hovered' : ''}`}
    onMouseEnter={onParentHover}
    onMouseLeave={onParentHoverEnd}
  >
    <button
      type="button"
      className="sales-location-parent"
      aria-expanded={hasMultipleLots ? isExpanded : undefined}
      onClick={onParentActivate}
    >
      <span className="sales-location-parent-icon" aria-hidden>
        <GeoAlt size={17} />
      </span>

      <span className="sales-location-parent-body">
        <span className="sales-location-parent-title d-block text-truncate">{title}</span>
        {showAddress && <span className="sales-location-parent-address d-block text-truncate">{address}</span>}
      </span>

      <span className="sales-location-parent-meta">
        {hasMultipleLots && (
          <span className="sales-location-lot-badge">
            {lotCount} LOT{lotCount === 1 ? '' : 'S'}
          </span>
        )}
        {region && <span className="sales-location-region">{region}</span>}
        {hasMultipleLots ? (
          <ChevronDown
            className={`sales-location-chevron sales-location-chevron--expandable${isExpanded ? '' : ''}`}
            size={16}
            aria-hidden
          />
        ) : (
          <ChevronRight className="sales-location-chevron" size={16} aria-hidden />
        )}
      </span>
    </button>

    {hasMultipleLots && (
      <SalesLocationLotsPanel
        title={title}
        sites={sites}
        isExpanded={isExpanded}
        hoveredSiteId={hoveredSiteId}
        onSiteHover={onSiteHover}
        onSiteHoverEnd={onSiteHoverEnd}
        onSiteSelect={onSiteSelect}
        location={location}
      />
    )}
  </article>
);

const SalesLocationSelectModal = ({
  show,
  onHide,
  salesLocations = [],
  onSiteSelect,
  defaultExpandedLocationId = null
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [hoveredId, setHoveredId] = useState(null);
  const [hoveredSiteId, setHoveredSiteId] = useState(null);
  const [expandedLocationId, setExpandedLocationId] = useState(null);
  const [sitesByLocationId, setSitesByLocationId] = useState({});
  const [sitesLoading, setSitesLoading] = useState(false);

  const siteCounts = useMemo(
    () =>
      Object.keys(sitesByLocationId).reduce((counts, key) => {
        counts[key] = sitesByLocationId[key].length;
        return counts;
      }, {}),
    [sitesByLocationId]
  );

  useEffect(() => {
    if (!show) {
      setSearchQuery('');
      setHoveredId(null);
      setHoveredSiteId(null);
      setExpandedLocationId(null);
      return;
    }

    let cancelled = false;

    const fetchSites = async () => {
      setSitesLoading(true);

      const { response, error } = await makeApiRequests({
        endpoint: ENDPOINTS.SITE_LIST,
        method: 'POST',
        requestBody: {
          populate: true,
          filter: {}
        }
      });

      if (cancelled) return;

      setSitesLoading(false);

      if (error) {
        toast.error(error);
        return;
      }

      setSitesByLocationId(buildSitesBySalesLocationId(response?.results || []));
    };

    fetchSites();

    return () => {
      cancelled = true;
    };
  }, [show]);

  useEffect(() => {
    if (show && defaultExpandedLocationId) {
      setExpandedLocationId(defaultExpandedLocationId);
    }
  }, [show, defaultExpandedLocationId]);

  const filteredLocations = useMemo(
    () => filterSalesLocations(salesLocations, searchQuery),
    [salesLocations, searchQuery]
  );

  useEffect(() => {
    if (expandedLocationId && !filteredLocations.some(location => location._id === expandedLocationId)) {
      setExpandedLocationId(null);
    }
  }, [filteredLocations, expandedLocationId]);

  const getSitesForLocation = location => sitesByLocationId[String(location._id)] || [];

  const handleLocationClick = location => {
    if (sitesLoading) return;

    const sites = getSitesForLocation(location);

    if (!sites.length) {
      toast.info('No site maps found for this sales location.');
      return;
    }

    if (sites.length === 1) {
      onSiteSelect(sites[0], location);
      return;
    }

    setExpandedLocationId(prev => (prev === location._id ? null : location._id));
  };

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static" size="md" dialogClassName="sales-location-select-modal">
      <Modal.Header closeButton className="border-bottom pb-3">
        <div className="pr-4">
          <Modal.Title className="h5 font-weight-bold mb-1">Select Sales Location</Modal.Title>
          <p className="text-muted small mb-0">
            Pick a branch to view its sitemap. Some branches have multiple lots.
          </p>
        </div>
      </Modal.Header>

      <Modal.Body className="pt-3 pb-2">
        <div className="sales-location-search-wrap mb-3">
          <Search size={16} className="sales-location-search-icon" aria-hidden />
          <Form.Control
            type="search"
            placeholder="Search by city, region, or address"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            disabled={sitesLoading}
            aria-label="Search sales locations"
          />
        </div>

        <div className="sales-location-list" role="listbox" aria-label="Sales locations">
          {sitesLoading ? (
            <Loader />
          ) : (
            <>
              {filteredLocations.map(location => {
                const locationId = location._id;
                const isExpanded = expandedLocationId === locationId;
                const isHovered = hoveredId === locationId;
                const title = getSalesLocationTitle(location);
                const address = formatSalesLocationAddress(location);
                const region = getSalesLocationRegion(location);
                const lotCount = siteCounts[String(locationId)] || 0;
                const showAddress = address && address !== title;
                const sites = getSitesForLocation(location);
                const hasMultipleLots = lotCount > 1;

                return (
                  <SalesLocationGroup
                    key={locationId}
                    location={location}
                    title={title}
                    address={address}
                    region={region}
                    showAddress={showAddress}
                    lotCount={lotCount}
                    hasMultipleLots={hasMultipleLots}
                    isExpanded={isExpanded}
                    isHovered={isHovered}
                    sites={sites}
                    hoveredSiteId={hoveredSiteId}
                    onParentActivate={() => handleLocationClick(location)}
                    onParentHover={() => setHoveredId(locationId)}
                    onParentHoverEnd={() => setHoveredId(null)}
                    onSiteHover={setHoveredSiteId}
                    onSiteHoverEnd={() => setHoveredSiteId(null)}
                    onSiteSelect={(site, loc) => onSiteSelect(site, loc)}
                  />
                );
              })}

              {!filteredLocations.length && (
                <p className="text-muted text-center small py-4 mb-0">
                  {salesLocations.length ? 'No locations match your search.' : 'No sales locations available.'}
                </p>
              )}
            </>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer className="border-top d-flex justify-content-between align-items-center py-3">
        <span className="text-muted small mb-0">
          {filteredLocations.length} location{filteredLocations.length === 1 ? '' : 's'}
        </span>
        <Button variant="link" className="text-dark font-weight-bold p-0 border-0" onClick={onHide} disabled={sitesLoading}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SalesLocationSelectModal;
